import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useNavigate } from 'react-router-dom'
import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import NotificationsIcon from '@mui/icons-material/Notifications'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AssignmentIcon from '@mui/icons-material/Assignment'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  Notification,
} from '../../services/notifications'
import { useSocket } from '../../contexts/SocketContext'

/**
 * Returns localized relative time string from date
 * @param dateStr - ISO date string
 * @param t - i18next translate function
 * @returns Relative time string
 */
function timeAgo(dateStr: string, t: TFunction, lang?: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return t('notifications.timeAgo.justNow')
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t('notifications.timeAgo.minutes', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('notifications.timeAgo.hours', { count: hours })
  const days = Math.floor(hours / 24)
  if (days < 7) return t('notifications.timeAgo.days', { count: days })
  return date.toLocaleDateString(lang)
}

/**
 * Returns icon component and color for notification type
 * @param type - Notification type
 * @returns Icon element and color
 */
function getTypeIcon(type: Notification['type']): { icon: React.ReactElement; color: string } {
  switch (type) {
    case 'warning':
      return { icon: <WarningIcon />, color: '#F59E0B' }
    case 'error':
      return { icon: <ErrorIcon />, color: '#EF4444' }
    case 'success':
      return { icon: <CheckCircleIcon />, color: '#10B981' }
    case 'task':
      return { icon: <AssignmentIcon />, color: '#6366F1' }
    case 'recommendation':
      return { icon: <LightbulbIcon />, color: '#F6B400' }
    case 'info':
    default:
      return { icon: <InfoIcon />, color: '#2563EB' }
  }
}

/**
 * Notification bell component with badge and popover
 * Polls for unread count every 30 seconds
 * @returns NotificationBell component
 */
function NotificationBell(): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { on, off } = useSocket()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  /**
   * Fetches unread notification count
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await getUnreadCount()
      setUnreadCount(response.data.count)
    } catch (error) {
      // Silently fail for polling
    }
  }, [])

  /**
   * Fetches notifications list
   */
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getNotifications({ limit: 20 })
      setNotifications(response.data.notifications)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Poll for unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount()
    intervalRef.current = setInterval(fetchUnreadCount, 30000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [fetchUnreadCount])

  // Real-time: listen for new notifications via socket
  useEffect(() => {
    const handleNewNotification = () => {
      setUnreadCount(prev => prev + 1)
      // Refresh list if popover is open
      if (anchorEl) {
        fetchNotifications()
      }
    }
    on('notification:new', handleNewNotification)
    return () => {
      off('notification:new', handleNewNotification)
    }
  }, [on, off, anchorEl, fetchNotifications])

  /**
   * Opens the notification popover
   */
  function handleOpen(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget)
    fetchNotifications()
  }

  /**
   * Closes the notification popover
   */
  function handleClose() {
    setAnchorEl(null)
  }

  /**
   * Handles clicking on a notification
   */
  async function handleNotificationClick(notification: Notification) {
    if (!notification.isRead) {
      try {
        await markAsRead(notification.id)
        setNotifications(prev =>
          prev.map(n => (n.id === notification.id ? { ...n, isRead: true } : n))
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      } catch (error) {
        console.error('Error marking notification as read:', error)
      }
    }

    // Navigate to related entity if available
    if (notification.relatedEntity) {
      const { type, id } = notification.relatedEntity
      handleClose()
      switch (type) {
        case 'task':
          navigate('/tasks')
          break
        case 'recommendation':
          navigate('/recommendations')
          break
        case 'yard':
          navigate(`/yards/${id}`)
          break
        case 'user':
          navigate('/users')
          break
        default:
          break
      }
    }
  }

  /**
   * Handles marking all notifications as read
   */
  async function handleMarkAllAsRead() {
    try {
      await markAllAsRead()
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all as read:', error)
    }
  }

  /**
   * Handles deleting a notification
   */
  async function handleDelete(event: React.MouseEvent, notificationId: string) {
    event.stopPropagation()
    try {
      await deleteNotification(notificationId)
      const deleted = notifications.find(n => n.id === notificationId)
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
      if (deleted && !deleted.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <IconButton onClick={handleOpen} size="medium" aria-label={t('notifications.title')}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 400, maxHeight: 500 },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('notifications.title')}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
              sx={{ textTransform: 'none' }}
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </Box>

        {/* Notification list */}
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('common.loading')}</Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {t('notifications.noNotifications')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 400, overflow: 'auto' }}>
            {notifications.map((notification, index) => {
              const { icon, color } = getTypeIcon(notification.type)
              return (
                <React.Fragment key={notification.id}>
                  <ListItem
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                      alignItems: 'flex-start',
                      py: 1.5,
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleDelete(e, notification.id)}
                        aria-label={t('button.delete.text')}
                        sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      <Box sx={{ color }}>{icon}</Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: notification.isRead ? 400 : 600,
                            pr: 3,
                          }}
                        >
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: '0.8rem', mb: 0.5, pr: 3 }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                          >
                            {timeAgo(notification.createdAt, t, i18n.language)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              )
            })}
          </List>
        )}
      </Popover>
    </>
  )
}

export default NotificationBell
